import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookOpenIcon, ChevronRightIcon, TagIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { getGenreColors } from '../utils/genreColors';
import { genreGlossaryData } from '../data/genreGlossaryData';
import { getReaderSubgenreTiers, toTaxonomySlug } from '../data/subgenreRegistry';
import { getSubgenreContentTags } from '../data/subgenreContentTags';

/**
 * Genre landing page — step one of the Genre -> Subgenre -> Tag -> Books drill.
 *
 * Deliberately sparse: title, description, book count, then Core and Crossover
 * subgenres. The Microgenre tier is no longer surfaced here; those entries are
 * folded into Core by getReaderSubgenreTiers.
 */
const GenrePage = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    genreSlug
  } = useParams<{
    genreSlug: string;
  }>();
  const glossaryEntry = useMemo(() => genreGlossaryData.find(g => g.slug === genreSlug), [genreSlug]);
  const registryGenre = useMemo(() => {
    if (!glossaryEntry) return undefined;
    // The glossary's display name is the registry key for every mapped genre.
    return getReaderSubgenreTiers(glossaryEntry.name).length > 0 ? glossaryEntry.name : undefined;
  }, [glossaryEntry]);
  const tiers = useMemo(() => registryGenre ? getReaderSubgenreTiers(registryGenre) : [], [registryGenre]);
  if (!glossaryEntry) {
    return <div className={`min-h-screen w-full ${dk.page}`} data-id="element-9255" data-mp="v41vk2xwtn3l">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-9256" data-mp="1023gktewbwhp">
          <TagIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-9257" data-mp="151ppiqnoyqkp" />
          <h1 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-9258" data-mp="14zmrbjcdxr4w">
            Genre not found
          </h1>
          <p className={`mb-6 ${dk.textSecondary}`} data-id="element-9259" data-mp="2bk5yzuqqs9ql">
            We couldn't find that genre.
          </p>
          <Link to="/genres" className={`font-medium ${rt.accentLink}`} data-id="element-9260" data-mp="1qsqu351n2wqu">
            Browse all genres
          </Link>
        </div>
      </div>;
  }
  const colors = getGenreColors(glossaryEntry.colorKey);
  return <div className={`min-h-screen w-full ${dk.page}`} data-id="element-9261" data-mp="20rgbkwpmif6k">
      {/* Header */}
      <div className={`py-6 px-4 ${dk.surface}`} data-id="element-9262" data-mp="1vrtzwrpg16zv">
        <div className="max-w-7xl mx-auto" data-id="element-9263" data-mp="1vt51qlzbw4oc">
          <nav aria-label="Breadcrumb" className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-9264" data-mp="18296fevnyrcc">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-9265" data-mp="1347o4w62aw1x">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-9266" data-mp="1htlvxztzp5f7" />
            <Link to="/genres" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-9267" data-mp="1d0stjilzu96a">
              Genres
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-9268" data-mp="p9ez974a83io" />
            <span className={dk.textPrimary} data-id="element-9269" data-mp="kbvmycr5a0td">{glossaryEntry.name}</span>
          </nav>

          <div className="flex items-start gap-3" data-id="element-9270" data-mp="2vzqsmaz51kzu">
            <div className="w-1.5 self-stretch rounded-full flex-shrink-0" style={{
            backgroundColor: colors.bg
          }} aria-hidden="true" data-id="element-9271" data-mp="2vy79mcm3kvud" />
            <div className="min-w-0" data-id="element-9272" data-mp="2lzrhc6bc3gja">
              <div className="flex flex-wrap items-center gap-3 mb-2" data-id="element-9273" data-mp="2r0e3fjkso1u3">
                <h1 className={`text-2xl md:text-3xl font-bold ${dk.textHeading}`} data-id="element-9274" data-mp="2qykufxmpl5ix">
                  {glossaryEntry.name}
                </h1>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap" style={{
                backgroundColor: dk.isDark ? colors.bgDark : colors.bgLight,
                color: dk.isDark ? colors.textDark : colors.text
              }} data-id="element-9275" data-mp="3jotg3zgge829">
                  {glossaryEntry.bookCount.toLocaleString()} books
                </span>
              </div>
              <p className={`text-base leading-relaxed max-w-3xl ${dk.textSecondary}`} data-id="element-9276" data-mp="2ekg334ze9g0t">
                {glossaryEntry.definition}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Subgenre tiers */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10" data-id="element-9277" data-mp="1zuic6ha2yc7x">
        {tiers.length === 0 ? <div className={`text-center py-16 rounded-xl border border-dashed ${dk.border} ${dk.surface}`} data-id="element-9278" data-mp="2xise6w9v8zz1">
            <BookOpenIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-9279" data-mp="2skh20ew3mddn" />
            <h2 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-9280" data-mp="2njb8ieum6m32">
              Subgenres coming soon
            </h2>
            <p className={dk.textSecondary} data-id="element-9281" data-mp="2jhy0pz7yo8ti">
              We're still building out the {glossaryEntry.name} taxonomy.
            </p>
          </div> : tiers.map(tier => <section key={tier.type} aria-labelledby={`tier-${tier.type}`} data-id="element-9282" data-mp="181ravsdjhxbt">
              <div className="flex items-center gap-2 mb-4" data-id="element-9283" data-mp="3jrpd6th6y8vz">
                <h2 id={`tier-${tier.type}`} className={`text-lg font-bold ${dk.textHeading}`} data-id="element-9284" data-mp="3ns9apy5ofein">
                  {tier.label}
                </h2>
                <span className={`text-xs px-2 py-0.5 rounded-full ${dk.badgeNeutral}`} data-id="element-9285" data-mp="1cok3tny96sv">
                  {tier.entries.length}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-9286" data-mp="bam7yg1wd1ky">
                {tier.entries.map(entry => {
            const slug = toTaxonomySlug(entry.displayLabel);
            const tagCount = getSubgenreContentTags(slug).length;
            return <Link key={entry.name} to={`/genres/${glossaryEntry.slug}/${slug}`} className={`block p-4 rounded-lg border ${dk.borderSubtle} ${dk.surfaceMuted} hover:shadow-md transition-shadow`} data-id="element-9287" data-mp="2h42nxsmmgc1r">
                      <div className="flex items-start justify-between gap-2 mb-1" data-id="element-9288" data-mp="3keqb69qyagug">
                        <h3 className={`font-semibold ${dk.textPrimary}`} data-id="element-9289" data-mp="3kh31ba6v3xil">
                          {entry.displayLabel}
                        </h3>
                        <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${dk.textMuted}`} data-id="element-9290" data-mp="3ted8okgk72i8" />
                      </div>
                      {tagCount > 0 && <p className={`text-xs ${dk.textMuted}`} data-id="element-9291" data-mp="77y8wfsv3f2r">
                          {tagCount} popular tags
                        </p>}
                      {entry.secondaryGenres && entry.secondaryGenres.length > 0 && <p className={`mt-1 text-xs ${dk.textMuted}`} data-id="element-9292" data-mp="uykgk5yyexc8">
                            Also in {entry.secondaryGenres.join(', ')}
                          </p>}
                    </Link>;
          })}
              </div>
            </section>)}
      </div>
    </div>;
};
export default GenrePage;